import { useAppStore } from '#imports';
import Stats from 'stats-gl';
import { Vector4 } from 'three';
import { OrbitControls } from 'three/addons';
import { computed, onBeforeUnmount, onMounted, provide, reactive, ref, watch } from 'vue';

import { urlSearchParams } from '@resn/gozer-misc';
import { usePane, useRafBool, useRenderer, useResize } from '@resn/gozer-vue';

import useThreeAnimations from './useThreeAnimations';
import { usePerformanceMetrics } from '~/composables/usePerformanceMetrics';
import { usePerformanceReporting } from '~/composables/usePerformanceReporting';
import { useSceneComposerPmndrs } from '~/composables/useSceneComposerPmndrs';
import { useThreeHelpers } from '~/composables/useThreeHelpers';
import { LAYER_BG, LAYER_FG } from '~/core/constants';

const useThreeMainScene = ({
    id = 'SceneMain',

    active = ref(true),
    enableStats = false,
    camera: cam,
}) => {
    const params = reactive({
        orbit: Boolean(urlSearchParams.get?.('orbit')),
        helpers: Boolean(urlSearchParams.get?.('helpers')),
        composer: urlSearchParams.get?.('composer') !== 'false',
    });

    const { renderer, camera: cameraDefault, orthoCamera, scene } = useRenderer();
    const composer = useSceneComposerPmndrs({ renderer, bloom: true, fxaa: true });

    cameraDefault.name = 'CameraDefault';
    scene.name = id;

    let camera = cameraDefault;
    let cameraScene = cam || cameraDefault.clone();

    const vViewOffset = new Vector4();

    const { addCameraHelpers, refreshHelpers, toggleHelpers } = useThreeHelpers(scene);
    const { updateAnimations, getAnimations, getAnimation } = useThreeAnimations();

    // Performance metrics
    const performanceMetrics = usePerformanceMetrics();
    const performanceReporting = usePerformanceReporting(performanceMetrics);

    const viewport = useResize((val) => resize(val), true);

    const canRender = computed(() => active.value);
    let orbit, stats;
    const useOrbit = ref(params.orbit);

    provide('orbit', useOrbit);

    onMounted(() => {
        init();

        window.renderer = renderer;
    });

    const init = () => {
        cameraDefault.fov = 50; // default camera scene fov
        cameraDefault.updateProjectionMatrix();

        if (params.helpers) addCameraHelpers();

        // Initialize stats-gl if enabled
        if (enableStats) {
            stats = new Stats({
                trackGPU: true,
                horizontal: false,
            });
            stats.init(renderer);
            // append the stats container to the body of the document
            document.body.appendChild(stats.dom);
        }

        setOrbit(useOrbit.value);
        resize(viewport);
    };

    const resize = ({ width, height }) => {
        vViewOffset.y = width;
        vViewOffset.z = height;
    };

    const setCameraParameters = ({ zoom = null, viewOffset = null, camera = null } = {}) => {
        const _camera = camera;
        if (!_camera) return;

        if (viewOffset) {
            vViewOffset.w = viewOffset.x;
            vViewOffset.x = viewOffset.y;
            const { w: x, x: y, y: vw, z: vh } = vViewOffset;
            _camera?.setViewOffset(vw, vh, x, y, vw, vh);
        }
        if (zoom) _camera.zoom = zoom;
        _camera.updateProjectionMatrix();
    };

    useRafBool(canRender, ({ delta, timestamp }) => {
        updateAnimations({ delta, timestamp });
        render();
        performanceMetrics.updateMetrics(timestamp, stats, renderer.info);
    });

    const render = ({ force = false } = {}) => {
        if (!canRender.value && !force) return;

        renderer.clear();

        const composerEnabled = composer && params.composer;
        const composerType = composer?.type;

        // PMNDRS composer handles everything except foreground
        if (composerEnabled && composerType === 'pmndrs') composer.render();
        // Otherwise render background + main scene (with or without Three.js composer)
        else {
            // Render background
            orthoCamera.layers.set(LAYER_BG);
            renderer.render(scene, orthoCamera);
            renderer.clearDepth();

            // Render main scene
            if (composerEnabled && composerType === 'three') {
                composer.render();
            } else {
                renderer.render(scene, camera);
            }
        }

        // Always render foreground last
        renderer.clearDepth();
        orthoCamera.layers.set(LAYER_FG);
        renderer.render(scene, orthoCamera);

        stats?.update();
    };

    // ― getters / setters
    const getCamera = () => cameraScene;
    const getScene = () => scene;

    const setOrbit = (bool) => {
        // Initialize OrbitControls only when explicitly needed (bool=true)
        // since it adds expensive event listeners for mouse/touch interactions
        if (!orbit && bool) orbit = new OrbitControls(cameraDefault, renderer.domElement);
        if (orbit) orbit.enabled = bool;

        camera = !bool && cameraScene ? cameraScene : cameraDefault;

        composer.setup({ renderer, scene, camera, orthoCamera });

        resize(viewport);
    };

    const setCamera = (cam) => {
        cameraScene = cam;

        setOrbit(useOrbit.value);
    };

    const refreshSceneHelpers = () => {
        if (params.helpers) refreshHelpers();
    };

    onBeforeUnmount(() => {
        destroy();
        if (orbit instanceof OrbitControls) orbit.dispose();

    });

    const destroy = () => {
        // disposeObject(scene);

        // Clean up stats
        if (stats) {
            stats.dom.remove();
            stats = null;
        }
    };

    watch(useOrbit, setOrbit);
    toggleHelpers(params.helpers);

    const appStore = useAppStore();
    const maxDpr = ref(appStore.maxDpr);
    watch(maxDpr, (val) => {
        appStore.maxDpr = val;
        const dpr = Math.min(window.devicePixelRatio, val);
        renderer.setPixelRatio(dpr);
    });

    usePane(
        [
            { name: 'maxDpr', value: maxDpr, options: { min: 1, max: 2, step: 0.1 } },
            {
                value: params,
                options: {
                    onChange: (ev) => {
                        if (ev.target.key == 'orbit') useOrbit.value = ev.value;
                        if (ev.target.key == 'helpers') toggleHelpers(ev.value);
                        if (ev.target.key == 'composer') params.composer = ev.value;
                    },
                },
            },
        ],
        { title: 'Scene', expanded: true }
    );

    return {
        getCamera,
        getScene,

        getAnimation,
        getAnimations,

        setOrbit,
        setCamera,
        setCameraParameters,

        refreshSceneHelpers,

        scene,
        orbit,
        composer,
        renderFn: render,
        performanceMetrics,
        performanceReporting,
    };
};

export default useThreeMainScene;
